export interface ClaudeBasicsPrompt {
  label: string;
  value: string;
}

export interface ClaudeBasicsLink {
  label: string;
  href: string;
}

export interface ClaudeBasicsStep {
  id: string;
  title: string;
  description: string;
  duration: string;
  whyItMatters: string;
  actions: string[];
  checkpoint: string;
  image?: {
    src: string;
    alt: string;
    caption: string;
  };
  prompts?: ClaudeBasicsPrompt[];
  table?: {
    headers: string[];
    rows: string[][];
  };
  note?: string;
  links?: ClaudeBasicsLink[];
}

export const FILE_REVIEW_PROMPT = `Read the attached meeting notes and pull out:

1. Decisions that were made
2. Action items, owners, and deadlines
3. Blockers
4. Unanswered questions

If the notes do not say, write "Not stated." Do not fill in the gaps.`;

export const ADAPTIVE_REASONING_PROTOCOL = `Use the Adaptive Reasoning Protocol:

Assess the request type, then apply the appropriate method:

If solving, analyzing, or debugging → Atom of Thought: Decompose into atomic reasoning units. For each atom:

State the logical component
Validate independence
Verify correctness
Then synthesize atoms into final answer.

If explaining, learning, or teaching → Feynman Loop: Explain as if teaching a curious beginner. For each cycle:

Use a concrete analogy
Flag confusion points
Ask questions that reveal gaps
Then compress into a teachable snapshot.

If both are needed → Chain them: First solve via Atom of Thought, then explain the solution via Feynman Loop.`;

export const CUSTOM_INSTRUCTIONS_EXAMPLE = `About me: I run a small bakery and write our weekly newsletter.
Who it is for: Local customers.
How to respond: Keep it warm and casual. Use short paragraphs and plain language.
One thing to always include: Suggest a seasonal item we can feature.`;

export const HOW_MIGHT_WE_PROMPT = `How might we turn my meeting notes into a clear weekly update?

Before you write, ask me up to three questions about the audience, format, and what matters most.

Then give me a reusable prompt I can run each week.`;

export const WEEKLY_UPDATE_PROMPT = `Turn these meeting notes into a short weekly update for my team.

Before you write, ask me up to three questions about the audience, format, and what matters most.

Create the update as an Artifact. Keep it under 300 words and use these sections:
- Decisions
- Progress
- Blockers
- Next steps`;

export const CALENDAR_SKILL_BRAINSTORM_PROMPT = `Look at my calendar for this week. What recurring jobs to be done could we turn into Agent Skills?

Give me three to five ideas. For each one, point to the calendar evidence, name the repeatable input and output, and explain why it is a good candidate.`;

export const PROJECT_INSTRUCTIONS = `Purpose: Turn meeting notes into a clear weekly team update.
Audience: My team. They need the important stuff quickly.
Output: Keep it under 300 words. Use decisions, progress, blockers, and next steps as the sections.
Quality bar: Make it direct, specific, and easy to scan. Use my saved example as the style reference.
Safety: Never invent an owner, deadline, or decision. If the source does not say, write "Not stated."`;

export const CLAUDE_BASICS_STEPS: ClaudeBasicsStep[] = [
  {
    id: "download-desktop",
    title: "Get Claude on your computer",
    description: "Install Claude Desktop for Mac or Windows, then sign in.",
    duration: "5 min",
    whyItMatters:
      "Claude Desktop keeps your chats, files, connectors, and longer workflows together. Set it up once, and we can do the rest of the guide in one place.",
    actions: [
      "Go to claude.ai/download and choose Mac or Windows.",
      "Run the installer and open Claude Desktop.",
      "Sign in with the Claude account you already use on the web. If this is your first time, create one.",
      "Take a quick look at the plan and tools available to you. This guide works with a free account, but some models and connectors may need a paid or workspace plan.",
    ],
    checkpoint: "Claude Desktop is open, and you are signed in.",
    image: {
      src: "/screenshots/masterclass/27-cowork-intro.png",
      alt: "Claude Desktop showing the option to open Claude's desktop workspace.",
      caption: "Keep Claude Desktop open for the rest of the guide. Your settings, chats, and connected tools will stay together.",
    },
    links: [
      { label: "Download Claude Desktop", href: "https://claude.ai/download" },
      { label: "Need more help installing it?", href: "/ai-agent-starter#download" },
    ],
  },
  {
    id: "choose-model",
    title: "Learn the screen and pick a model",
    description: "Find the four controls you will use most, then choose the right model for the job.",
    duration: "7 min",
    whyItMatters:
      "The controls tell Claude where the work belongs. The model picker changes the balance of speed and reasoning. Sonnet is the right starting point for most work. Use another model when the task gives you a reason to.",
    actions: [
      "Click the large message box. This is where you ask Claude for help.",
      "Find New Chat, usually shown as a plus. Start a new one when you change topics.",
      "Look at the left sidebar. That is where your earlier conversations live, and you can open any one to keep going.",
      "Find the plus inside the message box. We will use it later to attach a file or choose a connected tool.",
      "Find the current model name near the message box and open the model picker. What you see will depend on your plan and workspace settings.",
      "Choose Sonnet for this guide. For most everyday work, it gives you the best mix of speed and capability.",
    ],
    checkpoint: "You can start a chat, reopen an earlier one, attach a file, and explain why Sonnet is our default here.",
    image: {
      src: "/screenshots/masterclass/02-chat-tour.png",
      alt: "Claude home screen showing the left sidebar, message box, plus button, and model picker.",
      caption: "The labels may move over time. New Chat, history, attachments, and the model picker are still the four controls to know.",
    },
    table: {
      headers: ["Model", "Best for", "What to know"],
      rows: [
        ["Sonnet 5", "Everyday writing, analysis, questions, coding, and tool use", "Start here. It is the best balance of speed and intelligence for most work."],
        ["Opus 5", "Complex research, multi-step reasoning, and demanding professional work", "Use it when Sonnet is not getting you far enough. It thinks longer and uses more of your allowance."],
        ["Haiku 4.5", "Quick lookups, summaries, formatting, and simple high-volume tasks", "Use it when speed matters and the work is straightforward. It is not my first pick for a nuanced problem."],
        ["Fable 5", "Long-running agents, deep reasoning, and your highest-value complex work", "Use it for the hardest work. It is slower, uses your allowance fastest, and is only available on paid plans."],
      ],
    },
    note: "Models change. Trust the options in your picker, and start with Sonnet when you are not sure.",
    links: [
      { label: "Compare current Claude models", href: "https://platform.claude.com/docs/en/about-claude/models/overview" },
    ],
  },
  {
    id: "safe-defaults",
    title: "Set your privacy boundaries first",
    description: "Review model improvement and memory, then decide what stays out of any AI tool.",
    duration: "6 min",
    whyItMatters:
      "The settings matter, but your best safeguard is still a clear rule about sensitive information. Decide that rule before you bring real work into Claude.",
    actions: [
      "Open Settings from your name or initials, then open Privacy.",
      "Review Help improve our AI models and make an intentional choice. Turn it off if you do not want ordinary chats used for model improvement.",
      "Find Memory. It may be under Settings or Settings → Capabilities. Review what Claude remembers and decide whether you want it on.",
      "Use an incognito chat when you do not want a conversation saved to your ordinary history or memory.",
      "Use this rule: never paste passwords, account numbers, private health records, or confidential material you are not allowed to share.",
    ],
    checkpoint: "You made an intentional privacy choice and can say exactly what you will never share.",
    image: {
      src: "/screenshots/masterclass/09-privacy.png",
      alt: "Claude Privacy settings showing the model improvement control and links to memory preferences.",
      caption: "Claude is rolling out a newer Memory screen, so the labels on your account may look a little different.",
    },
    note: "Here is the test I use: if you would not hand it to a trusted contractor, do not paste it into a consumer AI account.",
    links: [
      {
        label: "Claude guidance for sensitive information",
        href: "https://support.claude.com/en/articles/8325621-i-would-like-to-input-sensitive-data-into-my-chats-with-claude-who-can-view-my-conversations",
      },
    ],
  },
  {
    id: "custom-instructions",
    title: "Stop repeating yourself with Custom Instructions",
    description: "Tell Claude who you are, who you serve, and how you want it to respond.",
    duration: "8 min",
    whyItMatters:
      "Think of Custom Instructions as Claude's onboarding note. Write the context once, and each new chat can start with your role, audience, standards, and preferred way of working.",
    actions: [
      "Choose where the context belongs. Use Profile preferences for guidance you want across Claude. Use Project instructions when it only applies to one kind of work.",
      "Open the right instructions area. In a Project, create or open the Project and find its instructions. For broader guidance, open Settings and look for Profile preferences or Instructions for Claude.",
      "Copy the example below, then replace the role, audience, tone, and standing rule with your own.",
      "Add the Adaptive Reasoning Protocol when you want Claude to switch between problem-solving and plain-language teaching on its own.",
      "Save it. Start a fresh chat that uses the instructions, then ask Claude to summarize what it knows about how to help you.",
    ],
    checkpoint: "A fresh chat already reflects your role, audience, response preferences, and reasoning approach.",
    image: {
      src: "/screenshots/masterclass/07-adaptive-reasoning.png",
      alt: "Claude's Instructions field with the Adaptive Reasoning Protocol pasted in.",
      caption: "Use broad instructions for guidance you always want. Keep workflow-specific context inside its Project.",
    },
    prompts: [
      { label: "Custom Instructions example", value: CUSTOM_INSTRUCTIONS_EXAMPLE },
      { label: "Adaptive Reasoning Protocol", value: ADAPTIVE_REASONING_PROTOCOL },
    ],
    links: [
      { label: "See the full Custom Instructions guide", href: "/ai-agent-starter#instructions" },
    ],
  },
  {
    id: "set-up-connectors",
    title: "Connect one source you already trust",
    description: "Let Claude use live information from one tool you approve.",
    duration: "7 min",
    whyItMatters:
      "A connector lets Claude work with a tool like Google Calendar, Gmail, Google Drive, Slack, or Microsoft 365. You choose the access. Claude gets useful context without making you copy and paste everything.",
    actions: [
      "Open Settings in Claude Desktop and find Connectors. Your plan or organization will decide which ones you can see.",
      "Pick one low-risk tool you already use, such as Google Calendar.",
      "Select Connect and finish that tool's sign-in and permission steps. Read the requested access before you approve it.",
      "Start a new chat and ask something only that connector can answer, such as: \"What is on my calendar tomorrow?\"",
      "Check Claude's answer against the source. If it is wrong or cannot connect, go back to Settings and review the connector's status and permissions.",
    ],
    checkpoint: "One connector is active, and Claude answered a question with live information you verified.",
    image: {
      src: "/screenshots/masterclass/21-connections-mcp.png",
      alt: "Claude Connectors settings listing available tools and their individual connection status.",
      caption: "You approve each connector separately. Connecting Calendar does not give Claude access to Gmail or Drive.",
    },
    note: "Start with read-only lookup work. Check the answer before you rely on it, and disconnect the tool anytime from its connector settings.",
    links: [
      { label: "See the full connector guide", href: "/ai-agent-starter#connectors" },
    ],
  },
  {
    id: "how-might-we",
    title: "Start with How Might We",
    description: "Frame the real problem so Claude asks useful questions before it starts writing.",
    duration: "6 min",
    whyItMatters:
      "How might we turns a vague command into a problem you can shape with Claude. A few good questions about the goal, audience, format, and constraints will beat a confident guess every time.",
    actions: [
      "Select New Chat so the task starts with a clean conversation.",
      "Copy the prompt below. Swap in something you actually do, or keep the weekly-update example for practice.",
      "Answer Claude's questions with real context and constraints.",
      "Ask Claude to show you the finished reusable prompt. Then run it in the same chat with a low-risk example or real information.",
      "Keep the chat open. In Step 7, we will improve the first answer instead of starting over.",
    ],
    checkpoint: "Claude asked useful questions, built a reusable prompt, and gave you a first answer worth improving.",
    prompts: [
      { label: "How Might We prompt", value: HOW_MIGHT_WE_PROMPT },
    ],
    note: "Compare these: \"Write my weekly update\" makes Claude guess. \"How might we turn my meeting notes into a weekly update?\" gives Claude an input and permission to ask better questions.",
    links: [
      { label: "Go deeper on How Might We", href: "/ai-agent-starter#how-might-we" },
    ],
  },
  {
    id: "improve-answer",
    title: "Coach the next version in the same chat",
    description: "Treat the first response as a draft, then tell Claude what will make it better.",
    duration: "5 min",
    whyItMatters:
      "You do not need a perfect prompt, and you do not need to start over. Claude remembers the conversation. Clear feedback is usually the fastest way to get to something useful.",
    actions: [
      "Name one thing the first draft got right.",
      "Name one thing that needs to change. Be specific about the tone, length, structure, or missing information.",
      "Ask for one revised version in the same chat.",
      "When it is good, tell Claude to use this version as the example next time.",
    ],
    checkpoint: "The second version is clearly better because of the feedback you gave.",
    prompts: [
      {
        label: "Feedback prompt",
        value: `This is close. Keep the warm opening.

Make the middle more personal, remove the generic praise, and shorten the whole note by about 20%.

Give me one revised version only.`,
      },
    ],
    note: "Save more than the result. A strong example shows Claude what good looks like the next time you run the work.",
  },
  {
    id: "work-from-file",
    title: "Give Claude one of your own files",
    description: "Attach a file and ask one clear question about what is actually inside it.",
    duration: "5 min",
    whyItMatters:
      "Claude gets much more useful when it can see the same source material you use. Start with extraction and organization. You should still own the high-stakes decisions.",
    actions: [
      "Choose a low-risk file, such as meeting notes, a public report, or a sample document.",
      "Click the plus in the message box and choose Add files or photos.",
      "Attach the file and send the review prompt below with it.",
      "Check at least two details against the source. Correct anything missing or unsupported.",
    ],
    checkpoint: "Claude used the file's actual contents, and you checked the answer against the source.",
    image: {
      src: "/screenshots/masterclass/04-share-files.png",
      alt: "Claude attachment menu with Add files or photos selected from the plus button.",
      caption: "Send the source and the request together. Claude needs both to do useful work.",
    },
    prompts: [{ label: "File-review prompt", value: FILE_REVIEW_PROMPT }],
  },
  {
    id: "create-artifact",
    title: "Turn the answer into an Artifact",
    description: "Create the finished work beside the chat, then revise it and take it with you.",
    duration: "7 min",
    whyItMatters:
      "Chat is where you give direction. The Artifact is the thing you will use: a document, checklist, table, or simple tool that stays visible while you improve it.",
    actions: [
      "In the chat with your file, ask Claude to turn the findings into a polished one-page Artifact.",
      "If no Artifact appears, open Settings → Capabilities and confirm Code execution and file creation is on.",
      "Ask for one change you can see, such as moving decisions to the top or shortening the introduction.",
      "When it is ready, use Copy or Download.",
    ],
    checkpoint: "You created an Artifact, changed it through the chat, and copied or downloaded the finished result.",
    image: {
      src: "/screenshots/masterclass/05-artifacts.png",
      alt: "Claude Artifacts library with a New artifact button and saved creations.",
      caption: "The Artifact keeps the deliverable separate from the conversation you used to make it.",
    },
    prompts: [
      {
        label: "Artifact prompt",
        value: `Turn these findings into a polished one-page Artifact.

Put decisions first, followed by action items, blockers, and unanswered questions.

Make it easy to scan. Do not add anything that is not in the source.`,
      },
    ],
    links: [
      {
        label: "How Claude Artifacts work",
        href: "https://support.claude.com/en/articles/9487310-what-are-artifacts-and-how-do-i-use-them",
      },
    ],
  },
  {
    id: "save-workflow",
    title: "Save the work as a reusable workflow",
    description: "Put a recurring task, its source material, and your definition of good inside a Claude Project.",
    duration: "13 min",
    whyItMatters:
      "One good answer saves a few minutes. A reusable workflow gives that time back every week. Projects keep the instructions, source material, examples, and conversations together so you do not restart from zero.",
    actions: [
      "Open your calendar for this week. If Google Calendar is connected, start a new chat and give Claude permission to use it for this exercise.",
      "No calendar connection? Take a screenshot of your week. Crop or hide anything sensitive you cannot share, then upload it with Add files or photos.",
      "Send the brainstorming prompt below. Ask Claude for three to five jobs backed by evidence from your week, not generic ideas.",
      "Pick one recurring, low-risk job with repeatable inputs and an output you can review. That is your Agent Skill candidate: the instructions, inputs, tools, examples, and quality checks needed to do the job again.",
      "Open Projects in the sidebar and create one named for the job. If you want to use the practice example as-is, call it Weekly Update. Projects are available on free accounts, with a limit on how many you can create.",
      "Add one example you like and paste the Project instructions below. Replace the Weekly Update details with your job, or keep them for practice.",
      "Add one fresh input and run the reusable workflow prompt. Replace the meeting-notes and team-update details with your job, or keep them for practice.",
      "Review the result. Save the strongest version as your good example, then update the instructions with anything Claude should repeat next time.",
    ],
    checkpoint: "You found at least three calendar-backed jobs, chose one low-risk Agent Skill candidate, and built a Project with instructions, source material, a finished output, and a good example for next week.",
    image: {
      src: "/screenshots/masterclass/06-project-setup.png",
      alt: "Claude Projects gallery with a New project button and existing project cards.",
      caption: "Keep each Project focused on one kind of work. The context stays cleaner, and the results get better.",
    },
    prompts: [
      { label: "Calendar-to-Agent-Skills brainstorm", value: CALENDAR_SKILL_BRAINSTORM_PROMPT },
      { label: "Project instructions", value: PROJECT_INSTRUCTIONS },
      { label: "Your first reusable workflow", value: WEEKLY_UPDATE_PROMPT },
    ],
    note: "Keep the loop simple: Run → Review → Save the best example → Update the instructions → Repeat.",
    links: [
      {
        label: "How Claude Projects work",
        href: "https://support.claude.com/en/articles/9517075-what-are-projects",
      },
    ],
  },
];
